/**
 * API Hook for Whenly Admin App
 *
 * Handles all API requests with cookie-based authentication.
 * Works with the external auth system (Google + Shopify store connections).
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { apiAccountKey, scopedApiRequest } from '../../../packages/shared/company-setup/request';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

interface UseApiReturn {
  fetchApi: <T = unknown>(path: string, options?: RequestInit) => Promise<T | null>;
  loading: boolean;
  error: string | null;
}

export function useApi(): UseApiReturn {
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { user, currentShop, currentOrganization, refreshAuth } = useAuth();
  const account = { userId: String(user?.id||''), organizationId:currentOrganization?.id||'',
    domain:currentShop && (!currentOrganization || currentShop.organizationId===currentOrganization.id) ? currentShop.domain : '' };
  const key=apiAccountKey(account);
  const latest=useRef({key,epoch:0,refreshAuth});
  latest.current={key,epoch:latest.current.epoch+(latest.current.key===key?0:1),refreshAuth};
  const epoch=latest.current.epoch;
  const mounted=useRef(true), sequence=useRef(0);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  const fetchApi = useCallback(async <T = unknown>(path:string, options:RequestInit={}):Promise<T|null> => {
    const active=()=>mounted.current && latest.current.key===key && latest.current.epoch===epoch;
    if(!active()) return null;
    const request=++sequence.current;setPending(n=>n+1);setError(null);
    try { return await scopedApiRequest<T>(API_URL,account,path,options,active,()=>latest.current.refreshAuth()); }
    catch(err) { if(active() && request===sequence.current)setError(err instanceof Error?err.message:'Request failed');return null; }
    finally { if(mounted.current)setPending(n=>Math.max(0,n-1)); }
  },[key,epoch]);
  return {fetchApi,loading:pending>0,error};
}

/**
 * Hook for fetching data on component mount
 */
export function useApiQuery<T>(path: string) {
  const { fetchApi, loading, error } = useApi();
  const [data, setData] = useState<T | null>(null);
  useEffect(()=>setData(null),[fetchApi,path]);

  const refetch = useCallback(async () => {
    const result = await fetchApi<T>(path);
    if (result) {
      setData(result);
    }
    return result;
  }, [fetchApi, path]);

  return { data, loading, error, refetch };
}

/**
 * Hook for mutations (POST, PUT, DELETE)
 */
export function useApiMutation<TData = unknown, TResponse = unknown>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST'
) {
  const { fetchApi, loading, error } = useApi();

  const mutate = useCallback(
    async (data?: TData): Promise<TResponse | null> => {
      return fetchApi<TResponse>(path, {
        method,
        body: data ? JSON.stringify(data) : undefined,
      });
    },
    [fetchApi, path, method]
  );

  return { mutate, loading, error };
}
